import { useState, useEffect } from "react";
import { Plus, Search, ArrowUpDown, List, BarChart3, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Issue, Sprint } from "@shared/schema";
import { format } from "date-fns";
import {
  SEVERITY_COLORS,
  STATUS_COLORS,
  PRIORITY_LABELS,
  SEVERITY_LABELS,
  STATUS_LABELS,
  MODULE_OPTIONS,
} from "@/lib/constants/issue-constants";
import { IssueQuickCaptureModal } from "@/components/issues/issue-quick-capture-modal";
import IssueMetrics from "./issue-metrics";

type SortField = "issueNumber" | "title" | "severity" | "priority" | "status" | "module" | "createdAt";
type SortDirection = "asc" | "desc";

export default function IssueList() {
  const [location] = useLocation();

  // Tab state - read from URL param if present
  const [activeTab, setActiveTab] = useState<string>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("tab") === "metrics" ? "metrics" : "issues";
  });

  // Simple state management (match testing checklists pattern)
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("active");
  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [selectedSprint, setSelectedSprint] = useState<string>("all");
  const [isQuickCaptureOpen, setIsQuickCaptureOpen] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Fetch all issues (uses default queryFn with auth headers)
  const { data: issues = [], isLoading } = useQuery<Issue[]>({
    queryKey: ["/api/crm/issues"],
  });

  // Fetch sprints for filter dropdown
  const { data: sprints = [] } = useQuery<Sprint[]>({
    queryKey: ["/api/project-manager/sprints"],
  });

  // Read URL parameters on initial mount only (one-way: URL → state)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    // Sprint parameter (primary use case)
    const sprintParam = searchParams.get('sprint');
    if (sprintParam && sprintParam.trim() !== '') {
      setSelectedSprint(sprintParam.trim());
    }

    // Severity parameter
    const severityParam = searchParams.get('severity');
    if (severityParam) {
      const validSeverities = ['critical', 'high', 'medium', 'low'];
      if (validSeverities.includes(severityParam.toLowerCase())) {
        setSelectedSeverity(severityParam.toLowerCase());
      }
    }

    // Status parameter
    const statusParam = searchParams.get('status');
    if (statusParam) {
      const validStatuses = [
        'all', 'active', 'inactive', 'open', 'investigating', 'fix_in_progress',
        'review', 'user_final_verify', 'closed', 'wont_fix', 'duplicate', 'deferred', 'rework'
      ];
      if (validStatuses.includes(statusParam.toLowerCase())) {
        setSelectedStatus(statusParam.toLowerCase());
      }
    }

    // Module parameter
    const moduleParam = searchParams.get('module');
    if (moduleParam && MODULE_OPTIONS.includes(moduleParam as any)) {
      setSelectedModule(moduleParam);
    }
  }, [location]); // Re-run when location changes

  // Toggle sort function
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Client-side filtering (match testing checklists pattern)
  const filteredIssues = issues.filter((issue) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        issue.title.toLowerCase().includes(searchLower) ||
        issue.issueNumber.toLowerCase().includes(searchLower) ||
        issue.errorMessage?.toLowerCase().includes(searchLower) ||
        issue.testId?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Severity filter
    if (selectedSeverity !== "all" && issue.severity !== selectedSeverity) {
      return false;
    }

    // Status filter
    if (selectedStatus === "active") {
      // Active = all issues still being worked on (excludes closed/resolved states)
      const inactiveStatuses = ["closed", "wont_fix", "duplicate"];
      if (inactiveStatuses.includes(issue.status)) {
        return false;
      }
    } else if (selectedStatus === "inactive") {
      // Inactive = only closed/resolved issues
      const inactiveStatuses = ["closed", "wont_fix", "duplicate"];
      if (!inactiveStatuses.includes(issue.status)) {
        return false;
      }
    } else if (selectedStatus !== "all" && issue.status !== selectedStatus) {
      return false;
    }

    // Module filter
    if (selectedModule !== "all" && issue.module !== selectedModule) {
      return false;
    }

    // Sprint filter
    if (selectedSprint !== "all" && issue.sprintId !== selectedSprint) {
      return false;
    }

    return true;
  }).sort((a, b) => {
    let aValue: string | Date | number;
    let bValue: string | Date | number;

    switch (sortField) {
      case "issueNumber":
        aValue = a.issueNumber.toLowerCase();
        bValue = b.issueNumber.toLowerCase();
        break;
      case "title":
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case "severity": {
        // Sort by severity priority: critical > high > medium > low
        const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        aValue = severityOrder[a.severity] || 0;
        bValue = severityOrder[b.severity] || 0;
        break;
      }
      case "priority": {
        // Sort by priority: p0 > p1 > p2 > p3
        const priorityOrder: Record<string, number> = { p0: 4, p1: 3, p2: 2, p3: 1 };
        aValue = priorityOrder[a.priority] || 0;
        bValue = priorityOrder[b.priority] || 0;
        break;
      }
      case "status":
        aValue = a.status.toLowerCase();
        bValue = b.status.toLowerCase();
        break;
      case "module":
        aValue = (a.module || "").toLowerCase();
        bValue = (b.module || "").toLowerCase();
        break;
      case "createdAt":
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Issues"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Issues" }]}
        actions={
          <Button
            size="sm"
            onClick={() => setIsQuickCaptureOpen(true)}
            data-testid="button-report-issue"
          >
            <Plus className="mr-2 h-4 w-4" />
            Report Issue
          </Button>
        }
      />

      {/* Quick Capture Modal */}
      <IssueQuickCaptureModal
        open={isQuickCaptureOpen}
        onOpenChange={setIsQuickCaptureOpen}
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4 pb-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="issues" className="flex items-center gap-2" data-testid="tab-issues">
                <List className="h-4 w-4" />
                Issues
              </TabsTrigger>
              <TabsTrigger value="metrics" className="flex items-center gap-2" data-testid="tab-metrics">
                <BarChart3 className="h-4 w-4" />
                Metrics
              </TabsTrigger>
            </TabsList>

            <TabsContent value="issues" className="space-y-4">
              {/* Search Bar */}
              <Card className="p-3">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by ID, title, error, or test ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-9"
                    data-testid="input-search-issues"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Clear search"
                      data-testid="button-clear-search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Card>

              {/* Inline Filters (match testing checklists pattern) */}
              <div className="flex flex-wrap items-center gap-4">
                {/* Severity Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="severity-filter" className="text-sm font-medium">
                    Severity:
                  </label>
                  <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
                    <SelectTrigger id="severity-filter" className="w-[180px]" data-testid="select-filter-severity">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Severities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="status-filter" className="text-sm font-medium">
                    Status:
                  </label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger id="status-filter" className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="fix_in_progress">Fix In Progress</SelectItem>
                      <SelectItem value="review">Review</SelectItem>
                      <SelectItem value="user_final_verify">Awaiting User Verification</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="wont_fix">Won't Fix</SelectItem>
                      <SelectItem value="duplicate">Duplicate</SelectItem>
                      <SelectItem value="deferred">Deferred</SelectItem>
                      <SelectItem value="rework">Rework</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Module Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="module-filter" className="text-sm font-medium">
                    Module:
                  </label>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger id="module-filter" className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modules</SelectItem>
                      {MODULE_OPTIONS.map((module) => (
                        <SelectItem key={module} value={module}>
                          {module}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Sprint Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="sprint-filter" className="text-sm font-medium">
                    Sprint:
                  </label>
                  <Select value={selectedSprint} onValueChange={setSelectedSprint}>
                    <SelectTrigger id="sprint-filter" className="w-[180px]" data-testid="select-filter-sprint">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sprints</SelectItem>
                      {sprints.map((sprint) => (
                        <SelectItem key={sprint.id} value={sprint.sprintNumber}>
                          {sprint.sprintNumber} {sprint.sprintName ? `- ${sprint.sprintName}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedSeverity("all");
                    setSelectedStatus("all");
                    setSelectedModule("all");
                    setSelectedSprint("all");
                  }}
                  disabled={
                    selectedSeverity === "all" &&
                    selectedStatus === "all" &&
                    selectedModule === "all" &&
                    selectedSprint === "all"
                  }
                >
                  Clear Filters
                </Button>

                {/* Results Count */}
                <div className="ml-auto text-sm text-muted-foreground">
                  {filteredIssues.length} {filteredIssues.length === 1 ? "issue" : "issues"}
                </div>
              </div>

              {/* Issues Table Card */}
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("issueNumber")}
                        >
                          ID
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("title")}
                        >
                          Title
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="w-[120px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("severity")}
                        >
                          Severity
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="w-[100px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("priority")}
                        >
                          Priority
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="w-[140px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("status")}
                        >
                          Status
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="w-[150px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("module")}
                        >
                          Module
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="w-[120px]">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("createdAt")}
                        >
                          Created
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          Loading issues...
                        </TableCell>
                      </TableRow>
                    ) : filteredIssues.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          No issues found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredIssues.map((issue) => (
                        <TableRow key={issue.id} className="cursor-pointer hover:bg-muted/50">
                          <TableCell>
                            <Link href={`/issues/${issue.id}`}>
                              <span className="hover:underline font-mono text-xs font-medium">
                                {issue.issueNumber}
                              </span>
                            </Link>
                          </TableCell>
                          <TableCell className="font-medium">
                            <Link href={`/issues/${issue.id}`}>
                              {issue.title}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={SEVERITY_COLORS[issue.severity]}
                            >
                              {SEVERITY_LABELS[issue.severity]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-mono">
                              {PRIORITY_LABELS[issue.priority]}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={STATUS_COLORS[issue.status]}
                            >
                              {STATUS_LABELS[issue.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{issue.module}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(issue.createdAt), "MMM d, yyyy")}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            <TabsContent value="metrics">
              <IssueMetrics />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
